/**
 * PublicFormsService
 * Anonymous (rate-limited) public form surface: render, track, validate, submit,
 * upload presign, lookups, and the self-service manage link (?token=).
 * Route: forms/api/public/forms
 */
import { BaseService } from '../base/BaseService';
import { unwrapFormsResponse as unwrap } from './unwrapFormsResponse';
/** Anonymous public forms service. A JWT, when present, enables member prefill. */
export class PublicFormsService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PublicFormsService.DEFAULT_SERVICE_PATH,
            basePath: PublicFormsService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ---- form ----
    /** GET api/public/forms/{code} — code = short code or slug. */
    async getForm(code) {
        return unwrap(await this.getFormRaw(code), `Form ${code} not found`);
    }
    getFormRaw(code) {
        return this.http.get(this.buildUrl(encodeURIComponent(code)));
    }
    /** POST api/public/forms/{code}/view — record a view (started=false) or a start (started=true). */
    async trackView(code, started = false) {
        const response = await this.trackViewRaw(code, started);
        return response.data ?? response.success;
    }
    trackViewRaw(code, started = false) {
        const body = { started };
        return this.http.post(this.buildUrl(`${encodeURIComponent(code)}/view`), body);
    }
    /** POST api/public/forms/{code}/validate — dry run (errors, amount due, capacity outcome). */
    async validate(code, request) {
        return unwrap(await this.validateRaw(code, request), `Failed to validate entry for ${code}`);
    }
    validateRaw(code, request) {
        return this.http.post(this.buildUrl(`${encodeURIComponent(code)}/validate`), request);
    }
    /**
     * POST api/public/forms/{code}/entries (reCAPTCHA + honeypot `website`).
     * When result.payment is set, POST payment.formData as hidden fields to payment.processUrl.
     */
    async submit(code, request) {
        return unwrap(await this.submitRaw(code, request), `Failed to submit entry for ${code}`);
    }
    submitRaw(code, request) {
        return this.http.post(this.buildUrl(`${encodeURIComponent(code)}/entries`), request);
    }
    /** POST api/public/forms/{code}/files/presign — then upload to uploadUrl with method/headers. */
    async presign(code, request) {
        return unwrap(await this.presignRaw(code, request), `Failed to presign upload for ${code}`);
    }
    presignRaw(code, request) {
        return this.http.post(this.buildUrl(`${encodeURIComponent(code)}/files/presign`), request);
    }
    /** GET api/public/forms/lookups/{locations|departments} */
    async getLookups(kind) {
        return (await this.getLookupsRaw(kind)).data ?? [];
    }
    getLookupsRaw(kind) {
        return this.http.get(this.buildUrl(`lookups/${kind}`));
    }
    /**
     * POST api/public/forms/{code}/inbound/{token} — flat `{ fieldKey: value }` body.
     * Normally called by third-party systems, exposed for completeness/testing.
     */
    async submitInbound(code, token, answers, idempotencyKey) {
        return unwrap(await this.submitInboundRaw(code, token, answers, idempotencyKey), `Failed inbound submit for ${code}`);
    }
    submitInboundRaw(code, token, answers, idempotencyKey) {
        return this.http.post(this.buildUrl(`${encodeURIComponent(code)}/inbound/${encodeURIComponent(token)}`), answers, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
    }
    // ---- entry status / manage link ----
    /** GET api/public/forms/entries/{ref}/status (no PII). */
    async getEntryStatus(ref) {
        return unwrap(await this.getEntryStatusRaw(ref), `Entry ${ref} not found`);
    }
    getEntryStatusRaw(ref) {
        return this.http.get(this.buildUrl(`entries/${encodeURIComponent(ref)}/status`));
    }
    /** GET api/public/forms/entries/{ref}?token= */
    async getManagedEntry(ref, token) {
        return unwrap(await this.getManagedEntryRaw(ref, token), `Entry ${ref} not found`);
    }
    getManagedEntryRaw(ref, token) {
        return this.http.get(this.buildUrlWithParams(`entries/${encodeURIComponent(ref)}`, { token }));
    }
    /** PUT api/public/forms/entries/{ref}?token= */
    async updateManagedEntry(ref, token, request) {
        return unwrap(await this.updateManagedEntryRaw(ref, token, request), `Failed to update entry ${ref}`);
    }
    updateManagedEntryRaw(ref, token, request) {
        return this.http.put(this.buildUrlWithParams(`entries/${encodeURIComponent(ref)}`, { token }), request);
    }
    /** POST api/public/forms/entries/{ref}/cancel?token= */
    async cancelManagedEntry(ref, token) {
        return unwrap(await this.cancelManagedEntryRaw(ref, token), `Failed to cancel entry ${ref}`);
    }
    cancelManagedEntryRaw(ref, token) {
        return this.http.post(this.buildUrlWithParams(`entries/${encodeURIComponent(ref)}/cancel`, { token }), {});
    }
    /** POST api/public/forms/entries/{ref}/pay?token= — returns the PayFast redirect. */
    async payManagedEntry(ref, token, request = {}) {
        return unwrap(await this.payManagedEntryRaw(ref, token, request), `Failed to start payment for entry ${ref}`);
    }
    payManagedEntryRaw(ref, token, request = {}) {
        return this.http.post(this.buildUrlWithParams(`entries/${encodeURIComponent(ref)}/pay`, { token }), request);
    }
    /** POST api/public/forms/entries/{ref}/accept-offer?token= — accept a waitlist offer. */
    async acceptOffer(ref, token, request = {}) {
        return unwrap(await this.acceptOfferRaw(ref, token, request), `Failed to accept offer for entry ${ref}`);
    }
    acceptOfferRaw(ref, token, request = {}) {
        return this.http.post(this.buildUrlWithParams(`entries/${encodeURIComponent(ref)}/accept-offer`, { token }), request);
    }
}
PublicFormsService.DEFAULT_SERVICE_PATH = 'forms';
PublicFormsService.BASE_PATH = 'api/public/forms';
//# sourceMappingURL=PublicFormsService.js.map